import "./ActionDropdown.less";
import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import {
    Dropdown,
    getTheme,
    Icon,
    IDropdownOption,
    IDropdownProps,
    ISelectableOption,
    ResponsiveMode,
    TooltipHost,
} from "office-ui-fabric-react";

export interface ActionDropdownProps extends LocalizeContextProps {
    options: IDropdownOption[];
    onSelectionChange: (option: IDropdownOption | undefined) => void;
    label: string;
    isLoading: boolean;
    isProjectSelected: boolean;
    placeholder: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    id?: string;
    selectedPurposeKey?: string | number;
    theme: string;
}

export interface ActionDropdownState {
    selectedKey: string | number;
    options: IDropdownOption[];
}

class ActionDropdown extends React.Component<ActionDropdownProps, ActionDropdownState> {
    constructor(props: ActionDropdownProps, context: ActionDropdownState) {
        super(props, context);

        this.state = {
            selectedKey: props.selectedPurposeKey ?? 0,
            options: [],
        };
        this.setDefaults();
    }

    componentDidMount(): void {
        this.setState({ options: this.state.options, selectedKey: this.state.selectedKey });
    }

    componentDidUpdate(
        prevProps: Readonly<ActionDropdownProps>,
        prevState: Readonly<ActionDropdownState>,
        snapshot?: any
    ) {
        if (this.props.options !== prevProps.options || this.state.options.length !== this.props.options.length) {
            this.setDefaults();
        }
    }

    private setDefaults(): void {
        const options = this.props.options;
        if (!this.props.options.length) {
            this.setState({ options: [], selectedKey: 0 });
            return;
        }
        this.setState({ options: options, selectedKey: this.state.selectedKey });
    }

    private getPlaceholder(): string {
        if (this.props.isLoading) {
            return this.props.translate("SHARED.ACTION_DROPDOWN.LOADING") as string;
        }

        if (!this.props.isProjectSelected) {
            return this.props.translate("SHARED.ACTION_DROPDOWN.NO_ACTION_SELECTED") as string;
        }

        if (!this.props.options.length) {
            return this.props.translate("SHARED.ACTION_DROPDOWN.NO_ITEMS") as string;
        }

        return this.props.placeholder;
    }

    private onSelectionChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        const selectedKey = option?.key === undefined ? 0 : option?.key;
        this.setState({ selectedKey });
        this.props.onSelectionChange(option);
    };

    onRenderOption: IDropdownProps["onRenderOption"] = (option?: ISelectableOption) => {
        if (!option) {
            return <></>;
        }
        const color = option.disabled ? getTheme().semanticColors.buttonTextDisabled : "";
        if (option.data?.tooltip) {
            return (
                <TooltipHost content={option.data.tooltip}>
                    <Icon iconName="Info" style={{ color }} />
                    <span style={{ color }}>{option.text}</span>
                </TooltipHost>
            );
        }
        return <span style={{ color }}>{option.text}</span>;
    };

    render(): JSX.Element {
        return (
            <Dropdown
                required={!!this.props.required}
                disabled={!!this.props.disabled}
                id={this.props.id ?? ""}
                label={this.props.label}
                className={`newforma-actionDropdown newforma-formSpacing${
                    this.props.className ? ` ${this.props.className}` : ""
                }`}
                selectedKey={this.state.selectedKey}
                onChange={this.onSelectionChange}
                options={this.state.options}
                placeholder={this.getPlaceholder()}
                onRenderOption={this.onRenderOption}
                responsiveMode={ResponsiveMode.large}
                calloutProps={{
                    className: `newforma-actionDropdown-callout newforma-actionDropdown-callout-${this.props.theme}`,
                }}
            />
        );
    }
}

export default withLocalize(ActionDropdown);
